import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, CheckCircle2, Database, FileText } from 'lucide-react';
import { useApp } from '../context/AppContext';
import StateFallback from '../components/StateFallback';

// A05 — Confirmed Recall: answer to "Has Milo been walked today?" derived
// entirely from backend recall state.
const MemoryAsk = () => {
  const navigate = useNavigate();
  const { recall, pet, memories, loading, error, reload } = useApp();

  if (loading) {
    return <StateFallback variant="loading" />;
  }
  if (error || !recall) {
    return (
      <StateFallback
        variant="error"
        message="We couldn't load this answer."
        onRetry={reload}
      />
    );
  }

  const isDefinite = recall.definite;
  const petName = pet ? pet.name : 'Milo';
  const eventId = recall.eventId;

  return (
    <div className="px-6 pt-8 pb-8 lp-enter">
      <h1
        className="font-serif-lp text-[40px] leading-none mb-2"
        style={{ color: 'var(--lp-cream)' }}
      >
        Memory
      </h1>
      <p className="text-[15px] mb-7" style={{ color: 'var(--lp-body)' }}>
        {isDefinite
          ? `Confirmed answers from stored memories for ${petName}.`
          : `A provisional answer from Lashachi Pet Memory.`}
      </p>

      <div className="lp-card p-5" style={{ borderColor: 'rgba(255,255,255,0.16)' }}>
        <h3
          className="font-serif-lp text-[22px] mb-1"
          style={{ color: 'var(--lp-cream)' }}
        >
          Ask Lashachi
        </h3>
        <p className="text-[14px] mb-5" style={{ color: 'var(--lp-body)' }}>
          {isDefinite
            ? `A question answered from ${petName}'s confirmed memories.`
            : 'A provisional answer that still needs your review.'}
        </p>

        <div className="flex items-start gap-3 mb-4">
          <span
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--lp-green-badge-bg)' }}
          >
            <HelpCircle size={18} style={{ color: 'var(--lp-green-badge-text)' }} />
          </span>
          <p className="text-[15px] pt-1.5" style={{ color: 'var(--lp-body)' }}>
            {recall.question}
          </p>
        </div>

        <div className="flex items-start gap-3 mb-4">
          <span
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--lp-green-badge-bg)' }}
          >
            <CheckCircle2 size={18} style={{ color: 'var(--lp-green-badge-text)' }} />
          </span>
          <p
            className="text-[15px] leading-relaxed pt-1"
            style={{ color: 'var(--lp-cream)' }}
          >
            {recall.answer}
          </p>
        </div>

        <div
          className="flex items-start gap-2.5 mb-3 pl-1"
          style={{ color: 'var(--lp-muted)' }}
        >
          <Database size={16} className="mt-0.5 flex-shrink-0" />
          <p className="text-[13px] leading-snug">
            {isDefinite ? 'User-confirmed' : 'Provisional'} · Supported by{' '}
            {recall.supportingCount} Ring motion observations
          </p>
        </div>

        {eventId && (
          <button
            onClick={() => navigate(`/memory/${eventId}`)}
            className="flex items-center gap-2.5 pl-1 lp-pressable"
            style={{ color: 'var(--lp-body)' }}
          >
            <FileText size={16} />
            <span className="text-[14px]">
              {isDefinite ? 'View evidence' : 'Review memory'}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default MemoryAsk;
